import { useCallback, useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Loader2, Search } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AdminNav, CountBadge, useUnmatchedCount } from "@/components/admin/AdminNav";
import { BonusDialog } from "@/components/admin/BonusDialog";
import { UnmatchedPayments } from "@/components/admin/UnmatchedPayments";
import { WebhookLog } from "@/components/admin/WebhookLog";
import { PlanDialog } from "@/components/admin/PlanDialog";
import { AdminLog } from "@/components/admin/AdminLog";
import { PromoAdmin } from "@/components/admin/PromoAdmin";
import { RevenueReport } from "@/components/admin/RevenueReport";
import { useApi } from "@/lib/api";
import { useMe } from "@/lib/me";
import { rm, tarikh } from "@/pages/AkaunPage";

const Users = () => {
  const api = useApi();
  const [q, setQ] = useState("");
  const [users, setUsers] = useState(null);
  const [sel, setSel] = useState(null);
  const [planUser, setPlanUser] = useState(null);
  const load = useCallback(() => api("get", "/admin/billing/users", null, { params: { q } }).then(setUsers).catch(() => setUsers([])), [api, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const onSaved = (uid, akaun) => {
    setUsers((us) => us.map((u) => (u.user_id === uid ? { ...u, akaun } : u)));
    setSel((s) => (s && s.user_id === uid ? { ...s, akaun } : s));
  };
  return (
    <div className="space-y-4">
      <label className="relative block max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-mute" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari e-mel…" data-testid="admin-users-search"
          className="w-full h-10 pl-9 pr-3 rounded-xl bg-surface border border-line text-sm focus:outline-none focus:border-[var(--q)]" />
      </label>
      {!users ? <Loader2 className="animate-spin text-q" /> : (
        <div className="rounded-xl border border-line overflow-x-auto surface">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Pengguna</TableHead><TableHead>Pelan</TableHead><TableHead className="text-right">Baki percuma</TableHead><TableHead className="text-right">Kuota pelan</TableHead><TableHead className="text-right">Jumlah baki</TableHead>
              <TableHead className="text-right">Guna bulan ini</TableHead><TableHead className="text-right">Tambahan</TableHead>
              <TableHead className="text-right">Jumlah jana</TableHead><TableHead className="text-right">Bayaran</TableHead><TableHead className="text-right">Kos AI</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}>
                  <TableCell><div className="text-sm">{u.email}</div><div className="text-xs text-mute">{u.role}</div></TableCell>
                  <TableCell className="text-sm">{u.akaun.plan_nama}{u.akaun.plan_expires_at && <div className="text-xs text-mute">tamat {tarikh(u.akaun.plan_expires_at)}</div>}</TableCell>
                  <TableCell className="text-right font-mono-data" data-testid={`admin-user-baki-percuma-${u.user_id}`}>{u.akaun.baki_percuma}</TableCell>
                  <TableCell className="text-right font-mono-data" data-testid={`admin-user-kuota-pelan-${u.user_id}`}>{u.akaun.plan === "percuma" ? "—" : <>{u.akaun.baki_pelan}<span className="text-mute">/{u.akaun.kuota_pelan}</span></>}</TableCell>
                  <TableCell className="text-right font-mono-data" data-testid={`admin-user-jumlah-${u.user_id}`}>{u.akaun.jumlah_baki}</TableCell>
                  <TableCell className="text-right font-mono-data">{u.akaun.guna_bulan_ini}</TableCell>
                  <TableCell className="text-right font-mono-data">{u.akaun.bonus}</TableCell>
                  <TableCell className="text-right font-mono-data">{u.jumlah_jana}</TableCell>
                  <TableCell className="text-right font-mono-data">{rm(u.bayaran_sen)}</TableCell>
                  <TableCell className="text-right font-mono-data">${u.kos_usd}</TableCell>
                  <TableCell><div className="flex gap-3"><button onClick={() => setSel(u)} className="text-sm text-q underline underline-offset-4 whitespace-nowrap" data-testid={`admin-user-manage-${u.user_id}`}>Urus</button>
                    <button onClick={() => setPlanUser(u)} className="text-sm text-q underline underline-offset-4 whitespace-nowrap" data-testid={`admin-user-plan-${u.user_id}`}>Tukar pelan</button></div></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {sel && <BonusDialog user={sel} onClose={() => setSel(null)} onSaved={onSaved} />}
      {planUser && <PlanDialog user={planUser} onClose={() => setPlanUser(null)} onSaved={onSaved} />}
    </div>
  );
};

const Payments = () => {
  const api = useApi();
  const [items, setItems] = useState(null);
  useEffect(() => { api("get", "/admin/billing/payments").then(setItems).catch(() => setItems([])); }, [api]);
  if (!items) return <Loader2 className="animate-spin text-q" />;
  return (
    <div className="rounded-xl border border-line overflow-x-auto surface" data-testid="admin-payments">
      <Table>
        <TableHeader><TableRow><TableHead>Tarikh</TableHead><TableHead>Pengguna</TableHead><TableHead>Produk</TableHead><TableHead className="text-right">Jumlah</TableHead><TableHead>Status</TableHead><TableHead>Mod</TableHead><TableHead>Rujukan OnPay</TableHead><TableHead>Resit</TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map((p) => (
            <TableRow key={p.id} data-testid={`admin-payment-row-${p.id}`}>
              <TableCell className="text-xs font-mono-data">{tarikh(p.created_at)}</TableCell>
              <TableCell className="text-sm">{p.email}</TableCell>
              <TableCell className="text-sm">{p.produk === "payg" ? `PAYG × ${p.qty}` : p.produk}</TableCell>
              <TableCell className="text-right font-mono-data">{rm(p.amount_sen)}</TableCell>
              <TableCell className="text-sm">{p.status}</TableCell>
              <TableCell className="text-xs">{p.mode}</TableCell>
              <TableCell className="text-xs font-mono-data">{p.gateway_ref || "—"}</TableCell>
              <TableCell className="text-xs" data-testid={`admin-payment-receipt-${p.id}`} title={p.resit_error || ""}>{p.resit_status || "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {items.length === 0 && <p className="p-4 text-sm text-mute">Tiada bayaran.</p>}
    </div>
  );
};

const UnmatchedBadge = () => <CountBadge n={useUnmatchedCount()} testid="admin-tab-unmatched-count" />;

export default function AdminBillingPage() {
  const [params] = useSearchParams();
  const tab = params.get("tab") || "users";
  const me = useMe();
  if (me && me.role !== "superadmin") return <Navigate to="/" replace />;
  if (!me) return <div className="p-10"><Loader2 className="animate-spin text-q" /></div>;
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10 space-y-8" data-testid="admin-billing-page">
      <header className="space-y-4">
        <AdminNav />
        <h1 className="font-display text-4xl sm:text-5xl text-ink">Pengguna & bayaran</h1>
      </header>
      <Tabs defaultValue={tab}>
        <TabsList><TabsTrigger value="users" data-testid="admin-tab-users">Pengguna & penggunaan</TabsTrigger><TabsTrigger value="payments" data-testid="admin-tab-payments">Rekod bayaran</TabsTrigger><TabsTrigger value="unmatched" data-testid="admin-tab-unmatched" className="gap-2">Tidak dipadankan <UnmatchedBadge /></TabsTrigger><TabsTrigger value="webhook" data-testid="admin-tab-webhook">Log webhook</TabsTrigger><TabsTrigger value="report" data-testid="admin-tab-report">Laporan hasil</TabsTrigger><TabsTrigger value="promo" data-testid="admin-tab-promo">Kod promosi</TabsTrigger><TabsTrigger value="log" data-testid="admin-tab-log">Log admin</TabsTrigger></TabsList>
        <TabsContent value="users" className="pt-6"><Users /></TabsContent>
        <TabsContent value="payments" className="pt-6"><Payments /></TabsContent>
        <TabsContent value="unmatched" className="pt-6"><UnmatchedPayments /></TabsContent>
        <TabsContent value="webhook" className="pt-6"><WebhookLog /></TabsContent>
        <TabsContent value="report" className="pt-6"><RevenueReport /></TabsContent>
        <TabsContent value="promo" className="pt-6"><PromoAdmin /></TabsContent>
        <TabsContent value="log" className="pt-6"><AdminLog /></TabsContent>
      </Tabs>
    </div>
  );
}
